// src/components/map/layers/CrewPresenceLayer.ts
// Leaflet presentation owner for Big Blue Crew presence markers.
// Data subscriptions and own-presence publishing stay in MapView so they remain warm.

// @ts-ignore - local editor type noise for leaflet; runtime import is valid in this project
import L from "leaflet";
import type { CrewPresence } from "../../../types";

const escapeHtml = (value: unknown) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#039;");

const formatLastSeen = (lastSeenAt?: number | null) => {
  const ageMs = Math.max(0, Date.now() - Number(lastSeenAt || 0));
  const mins = Math.floor(ageMs / 60000);
  if (mins < 1) return "Online now";
  if (mins === 1) return "Active 1m ago";
  if (mins < 60) return `Active ${mins}m ago`;
  return "Recently active";
};

const createPresenceIcon = (presence: CrewPresence) => {
  const avatar =
    presence.avatarUrl ||
    "data:image/svg+xml;utf8," +
      encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96">
           <rect width="96" height="96" rx="24" fill="#7c3aed"/>
           <circle cx="48" cy="38" r="16" fill="#c4b5fd"/>
           <path d="M20 86c6-18 20-26 28-26s22 8 28 26" fill="#c4b5fd"/>
         </svg>`,
      );

  return (L as any).divIcon({
    className: "bg-transparent",
    html: `
      <div class="relative h-12 w-12">
        <div class="absolute inset-0 rounded-full bg-emerald-300/40 blur-[2px]"></div>
        <div class="absolute inset-1 rounded-full border-2 border-emerald-200 bg-[#151427] shadow-[0_0_18px_rgba(110,255,210,0.65)] overflow-hidden">
          <img src="${escapeHtml(avatar)}" alt="Crew location" class="h-full w-full object-cover" />
        </div>
        <div class="absolute -bottom-0.5 -right-0.5 grid h-4 w-4 place-items-center rounded-full border border-white bg-emerald-400 text-[9px] shadow-lg">●</div>
      </div>
    `,
    iconSize: [48, 48],
    iconAnchor: [24, 24],
    popupAnchor: [0, -22],
  });
};

type CachedPresenceMarker = {
  marker: any;
  signature: string;
};

export type CrewPresenceLayerController = {
  update: (args: {
    layer: any;
    presence: CrewPresence[];
    isDarkMode?: boolean;
    force?: boolean;
  }) => void;
  forEachMarker: (visitor: (marker: any) => void) => void;
  resetForLayerRebuild: () => void;
  dispose: (layer?: any) => void;
};

export const createCrewPresenceLayerController = (): CrewPresenceLayerController => {
  const markerCache = new Map<string, CachedPresenceMarker>();
  let lastSignature = "";

  const clearCache = (layer?: any) => {
    markerCache.forEach(({ marker }) => {
      try {
        layer?.removeLayer?.(marker);
      } catch {}
    });
    markerCache.clear();
    lastSignature = "";
  };

  return {
    update({ layer, presence, isDarkMode = false, force = false }) {
      if (!layer) return;

      const visible = Array.isArray(presence)
        ? presence.filter(
            (item) =>
              item?.isOnline === true &&
              item?.locationSharing === "crew" &&
              item?.approxLocation &&
              Number.isFinite(Number(item.approxLocation.lat)) &&
              Number.isFinite(Number(item.approxLocation.lng)),
          )
        : [];

      // Crew presence is geographic truth for accepted crewmates. Never move a
      // crewmate just to solve a screen-space collision.
      const signature = JSON.stringify({
        dark: !!isDarkMode,
        items: visible.map((item) => ({
          uid: item.uid,
          lat: Number(item.approxLocation?.lat || 0).toFixed(5),
          lng: Number(item.approxLocation?.lng || 0).toFixed(5),
          online: !!item.isOnline,
          lastSeenAt: Math.floor(Number(item.lastSeenAt || 0) / 30000),
          avatar: item.avatarUrl || "",
        })),
      });

      if (!force && signature === lastSignature) return;

      const nextKeys = new Set<string>();

      visible.forEach((item) => {
        const uid = String(item.uid || "").trim();
        const lat = Number(item.approxLocation?.lat);
        const lng = Number(item.approxLocation?.lng);
        if (!uid || !Number.isFinite(lat) || !Number.isFinite(lng)) return;

        const markerSignature = JSON.stringify({
          uid,
          lat: lat.toFixed(6),
          lng: lng.toFixed(6),
          name: item.displayName || "",
          avatar: item.avatarUrl || "",
          online: !!item.isOnline,
          bucket: Math.floor(Number(item.lastSeenAt || 0) / 30000),
        });
        nextKeys.add(uid);

        const cached = markerCache.get(uid);
        if (cached && cached.signature === markerSignature) {
          try {
            // Preserve the existing divIcon DOM node across map route reveals.
            cached.marker.setLatLng?.([lat, lng]);
            if (!layer.hasLayer?.(cached.marker)) cached.marker.addTo(layer);
          } catch {}
          return;
        }

        if (cached) {
          try {
            layer.removeLayer?.(cached.marker);
          } catch {}
          markerCache.delete(uid);
        }

        const safeName = escapeHtml(item.displayName || "Crewmate");
        const safeStatus = escapeHtml(formatLastSeen(item.lastSeenAt));
        const marker = (L as any).marker([lat, lng], {
          icon: createPresenceIcon(item),
          pane: "markersPane",
          keyboard: false,
          riseOnHover: true,
          zIndexOffset: 1800,
        });

        marker.bindPopup?.(
          `<div class="min-w-[150px] p-1">
            <div class="text-sm font-extrabold text-slate-900">${safeName}</div>
            <div class="mt-0.5 text-xs text-slate-600">Crew • ${safeStatus}</div>
            <div class="mt-2 rounded-full bg-slate-100 px-2 py-1 text-center text-[11px] font-bold text-slate-700">Crew map presence</div>
          </div>`,
          { offset: [0, -12] },
        );

        marker.addTo(layer);
        markerCache.set(uid, { marker, signature: markerSignature });
      });

      Array.from(markerCache.keys()).forEach((uid) => {
        if (nextKeys.has(uid)) return;
        const cached = markerCache.get(uid);
        if (cached) {
          try {
            layer.removeLayer?.(cached.marker);
          } catch {}
        }
        markerCache.delete(uid);
      });

      lastSignature = signature;
    },

    forEachMarker(visitor) {
      markerCache.forEach(({ marker }) => visitor(marker));
    },

    resetForLayerRebuild() {
      markerCache.clear();
      lastSignature = "";
    },

    dispose(layer) {
      clearCache(layer);
    },
  };
};
